import type { ChildProcess } from 'node:child_process';
import { killChildProcessTreeGracefully } from '../../agents/acp/clients/kill-process-tree.js';
import { logImmediate } from '../../log.js';
import { cleanupMergedLogDirForServer } from './cleanup-merged-log-dir.js';
import { PREVIEW_ENVIRONMENT_STOP_GRACE_MS } from './constants.js';
import {
  abortPreviewEnvironmentSpawn,
  bumpPreviewEnvironmentSpawnGeneration,
  clearPreviewEnvironmentPoll,
  clearPreviewEnvironmentTails,
  discardPreviewEnvironmentAbortController,
  previewEnvironmentDisplayTitle,
  sendPreviewEnvironmentManagerStatus,
  snapshotPreviewEnvironmentTails,
  type PreviewEnvironmentManagerState,
} from './state.js';
import type { PreviewEnvironmentManagerRuntime } from './runtime.js';

function terminateRunningPreviewEnvironmentProcess(proc: ChildProcess): void {
  // Keep exit listeners attached so the wire handler can log when tree-kill is not used.
  void killChildProcessTreeGracefully(proc, PREVIEW_ENVIRONMENT_STOP_GRACE_MS);
}

export function stopPreviewEnvironment(
  runtime: PreviewEnvironmentManagerRuntime,
  environmentId: string,
): void {
  const { state, getWs } = runtime;
  const title = previewEnvironmentDisplayTitle(state, environmentId);
  const tails = snapshotPreviewEnvironmentTails(state, environmentId);
  const proc = state.processes.get(environmentId);
  const hadRunningProcess = proc != null;
  const stoppedPid = proc?.pid;
  logImmediate(
    `[preview-environment] Stopped ${title}${hadRunningProcess ? ` (pid=${stoppedPid ?? '?'})` : ''}.`,
  );
  if (hadRunningProcess) {
    sendPreviewEnvironmentManagerStatus(getWs, environmentId, 'stopping', undefined, tails);
  }
  bumpPreviewEnvironmentSpawnGeneration(state, environmentId);
  clearPreviewEnvironmentPoll(state, environmentId);

  if (proc) {
    // Tree-kill the full command tree first. abort() only SIGTERMs the shell wrapper and can
    // orphan npm/node children before tree-kill runs.
    state.processes.delete(environmentId);
    discardPreviewEnvironmentAbortController(state, environmentId);
    terminateRunningPreviewEnvironmentProcess(proc);
  } else {
    abortPreviewEnvironmentSpawn(state, environmentId);
  }

  clearPreviewEnvironmentTails(state, environmentId);
  state.pipedCaptureByServerId.delete(environmentId);
  cleanupMergedLogDirForServer(state.mergedLogCleanupDirByServerId, environmentId);
  sendPreviewEnvironmentManagerStatus(getWs, environmentId, 'stopped', undefined, tails);
}

export function detachPreviewEnvironmentProcess(
  state: PreviewEnvironmentManagerState,
  environmentId: string,
): void {
  state.processes.delete(environmentId);
  state.pipedCaptureByServerId.delete(environmentId);
  state.abortControllersByServerId.delete(environmentId);
  state.mergedLogCleanupDirByServerId.delete(environmentId);
}

/** Only detach when this child is still the managed process (avoids restart races). */
export function detachPreviewEnvironmentProcessIfMatches(
  state: PreviewEnvironmentManagerState,
  environmentId: string,
  proc: ChildProcess,
): void {
  if (state.processes.get(environmentId) !== proc) return;
  detachPreviewEnvironmentProcess(state, environmentId);
}
